// Pure logica voor de klant-orderbevestiging (epic 2). Bouwt de afgeschermde snapshot
// (geen marge/leverancier) en detecteert drift t.o.v. de huidige order-staat.

export interface ConfirmationItem {
  name: string;
  quantity: number;
}

export interface ConfirmationPackage {
  name: string;
  items: ConfirmationItem[];
  /** Eindprijs incl. btw voor de klant (door de caller berekend met gift-pricing). */
  eindprijs: number;
}

export interface ConfirmationClient {
  company_name: string;
  contact_name?: string | null;
  address?: string | null;
}

export interface ConfirmationSnapshot {
  client: ConfirmationClient;
  packages: ConfirmationPackage[];
  eindprijs_totaal: number;
}

/**
 * Bouwt de orderbevestiging-snapshot per project: aggregeert alle (definitieve) pakketten.
 * Bevat UITSLUITEND klantgegevens + pakketinhoud + eindprijs. Geen marge, geen leverancier,
 * geen inkoopprijzen (FR-4.5) — de caller levert al-afgeschermde pakket-data aan.
 */
export function buildConfirmationSnapshot(
  client: ConfirmationClient,
  packages: ConfirmationPackage[]
): ConfirmationSnapshot {
  const eindprijs_totaal = packages.reduce((sum, p) => sum + (p.eindprijs || 0), 0);
  return {
    client: {
      company_name: client.company_name,
      contact_name: client.contact_name ?? null,
      address: client.address ?? null,
    },
    packages: packages.map((p) => ({
      name: p.name,
      items: p.items.map((it) => ({ name: it.name, quantity: it.quantity })),
      eindprijs: Math.round((p.eindprijs || 0) * 100) / 100,
    })),
    eindprijs_totaal: Math.round(eindprijs_totaal * 100) / 100,
  };
}

/** Vergelijkt de inhoudelijk-significante velden; true = de order is gewijzigd t.o.v. de snapshot. */
export function snapshotsDiffer(a: ConfirmationSnapshot, b: ConfirmationSnapshot): boolean {
  return JSON.stringify(normalize(a)) !== JSON.stringify(normalize(b));
}

function normalize(s: ConfirmationSnapshot) {
  return {
    client: {
      company_name: s.client.company_name,
      contact_name: s.client.contact_name ?? null,
      address: s.client.address ?? null,
    },
    eindprijs_totaal: s.eindprijs_totaal,
    packages: [...s.packages]
      .sort((x, y) => x.name.localeCompare(y.name))
      .map((p) => ({
        name: p.name,
        eindprijs: p.eindprijs,
        items: [...p.items].sort((x, y) => x.name.localeCompare(y.name)),
      })),
  };
}
